import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { uploadFileMultipart } from "@/lib/upload";
import type { Tier } from "@colab/shared";

export type UploadTierBadgeInput = {
  programId: string;
  tierId: string;
  file: File;
};

type UploadResponse = { path: string; tier: Tier };

// POST /api/admin/tiers/programs/:programId/tiers/:tierId/badge   (multipart)
//
// Deliberately atomic (legacy parity) rather than the bucket-4 three-step
// flow: tier badges are 1MB image-only and splitting upload + patch into
// independent steps would create a new orphan-row failure mode. The worker
// proxies the bytes through services/storage.ts::uploadTierBadge (already
// validates size + image/* type) and patches tier.badge_path in the same
// handler.
//
// Sub-bucket 15 — second consumer of `uploadFileMultipart` (the single-POST
// multipart helper in `@/lib/upload`). Tier-badge was the original
// inspiration for that shape; admin MDF doesn't add a new consumer (the
// legacy approve/decline/mark-spent dialogs don't upload receipts — the
// partner-side bucket 9 attachment flow already handles that via the 3-step
// `uploadFileViaSignedUrl`).
//
// No optimistic update — file uploads aren't reliable enough for an
// optimistic UI flip, and the user expects "uploading…" feedback anyway.
export function useUploadTierBadge() {
  const queryClient = useQueryClient();
  return useMutation<UploadResponse, ApiClientError, UploadTierBadgeInput>({
    mutationFn: ({ programId, tierId, file }) =>
      uploadFileMultipart<UploadResponse>(file, {
        endpoint: `/admin/tiers/programs/${programId}/tiers/${tierId}/badge`,
      }),
    onSuccess: (_data, { programId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.tiers.program(programId) });
      // Tier badge change ripples to partner cards everywhere.
      queryClient.invalidateQueries({ queryKey: queryKeys.partners.all });
    },
    onError: (err) => {
      toast.error(err.message ?? "Failed to upload badge");
    },
  });
}
